import type { SocialParameterName } from './socialParameters';
import { PointsDistribution, getEmptyPointsDistribution } from '../utils/socialActivityXP';

/**
 * Interaction → Capacity attribution matrix (SO5, SOCIAL_DOMAIN_SPEC §5/§10).
 * Each activity type maps to the social capacities it exercises, with relative
 * weights. QuickLog uses this to AUTO-distribute an interaction's points across
 * capacities (no manual allocation) — the two-tap daily loop.
 *
 * Conflict Navigation + (mostly) Boundaries are intentionally sparse here —
 * they're trained primarily via Abilities practice (SO6), not casual hangouts.
 */
export const INTERACTION_ATTRIBUTION: Record<string, Partial<Record<SocialParameterName, number>>> = {
  // In-person
  'Coffee/Tea': { 'Relationship Depth': 1, 'Social Confidence': 1 },
  'Meal': { 'Relationship Depth': 1, 'Social Confidence': 1 },
  'Walk': { 'Relationship Depth': 1, 'Communication': 1 },
  'Hangout': { 'Social Confidence': 1, 'Relationship Depth': 1 },
  'Visit': { 'Relationship Depth': 2, 'Trust & Reliability': 1 },
  // Remote
  'Phone Call': { 'Communication': 2 },
  'Video Call': { 'Communication': 2 },
  'Text Chat': { 'Communication': 2 },
  'Voice Message': { 'Communication': 2 },
  // Shared activity
  'Workout': { 'Social Confidence': 1, 'Relationship Depth': 1 },
  'Game Night': { 'Social Confidence': 2 },
  'Movie/Show': { 'Relationship Depth': 1, 'Social Confidence': 1 },
  'Sports': { 'Social Confidence': 2 },
  'Hobby Session': { 'Social Confidence': 1, 'Relationship Depth': 1 },
  'Learning Together': { 'Communication': 1, 'Relationship Depth': 1 },
  'Work Session': { 'Communication': 1, 'Boundaries': 1 },
  // Special
  'Party/Event': { 'Social Confidence': 2 },
  'Trip/Travel': { 'Relationship Depth': 2, 'Social Confidence': 1 },
  'Celebration': { 'Relationship Depth': 1, 'Social Confidence': 1 },
  'Support Session': { 'Emotional Intelligence': 2, 'Trust & Reliability': 1 },
  'Deep Conversation': { 'Relationship Depth': 2, 'Emotional Intelligence': 1 },
  'Meeting': { 'Communication': 1, 'Boundaries': 1 },
  'Other': { 'Relationship Depth': 1 },
};

/** Fallback when a type isn't in the matrix. */
export const DEFAULT_ATTRIBUTION: Partial<Record<SocialParameterName, number>> = {
  'Relationship Depth': 1,
};

/** The capacities an interaction type credits (for a UI preview). */
export function capacitiesForType(activityType: string): SocialParameterName[] {
  const weights = INTERACTION_ATTRIBUTION[activityType] ?? DEFAULT_ATTRIBUTION;
  return Object.keys(weights) as SocialParameterName[];
}

/**
 * Split `points` across an interaction type's capacities by weight, as whole
 * points that sum EXACTLY to `points` (largest-remainder method).
 */
export function distributeByType(activityType: string, points: number): PointsDistribution {
  const dist = getEmptyPointsDistribution();
  const pts = Math.max(0, Math.floor(points || 0));
  if (pts === 0) return dist;

  const weights = INTERACTION_ATTRIBUTION[activityType] ?? DEFAULT_ATTRIBUTION;
  const entries = Object.entries(weights) as [SocialParameterName, number][];
  const totalW = entries.reduce((s, [, w]) => s + w, 0);
  if (totalW <= 0) {
    dist['Relationship Depth'] += pts;
    return dist;
  }

  let allocated = 0;
  const remainders: { cap: SocialParameterName; rem: number }[] = [];
  for (const [cap, w] of entries) {
    const exact = pts * (w / totalW);
    const base = Math.floor(exact);
    dist[cap] += base;
    allocated += base;
    remainders.push({ cap, rem: exact - base });
  }

  // hand out the leftover to the largest fractional remainders
  remainders.sort((a, b) => b.rem - a.rem);
  const leftover = pts - allocated;
  for (let i = 0; i < leftover; i++) {
    dist[remainders[i % remainders.length].cap] += 1;
  }

  return dist;
}
